import React, { useEffect, useState } from "react";
import { CButton, CContainer } from "@coreui/react-pro";
import PublicSiteShell from "../components/public/PublicSiteShell";
import PublicRoadmapGuide from "../components/public/PublicRoadmapGuide";
import publicRoadmapContent from "../content/publicRoadmapContent";
import "../styles/public-experience.css";
import "../styles/public-roadmap.css";

function resolveInitialLanguage() {
  const stored =
    typeof window !== "undefined" ? window.localStorage.getItem("publicRoadmapLanguage") : "";
  const byStorage = String(stored || "").toLowerCase();
  if (publicRoadmapContent.locales[byStorage]) return byStorage;

  const browserLanguage =
    typeof navigator !== "undefined" ? String(navigator.language || "").slice(0, 2).toLowerCase() : "";
  if (publicRoadmapContent.locales[browserLanguage]) return browserLanguage;

  return publicRoadmapContent.defaultLanguage;
}

function resolveInitialPhaseId() {
  const locale = publicRoadmapContent.locales[publicRoadmapContent.defaultLanguage];
  const inProgressPhase = locale?.page?.phases?.find((phase) => phase.status === "in_progress");
  return inProgressPhase?.id || locale?.page?.phases?.[0]?.id || "core-platform-build";
}

function interpolateLabel(template, replacements) {
  return Object.entries(replacements).reduce((label, [key, value]) => {
    return String(label).replace(new RegExp(`\\{${key}\\}`, "g"), String(value));
  }, String(template || ""));
}

export default function PublicRoadmapPage() {
  const [language, setLanguage] = useState(resolveInitialLanguage);
  const [activePhaseId, setActivePhaseId] = useState(resolveInitialPhaseId);

  const locale = publicRoadmapContent.locales[language];
  const languageMeta =
    publicRoadmapContent.languages.find((entry) => entry.code === language) ||
    publicRoadmapContent.languages[0];
  const activePhase =
    locale.page.phases.find((phase) => phase.id === activePhaseId) || locale.page.phases[0];
  const activePhaseIndex = Math.max(
    locale.page.phases.findIndex((phase) => phase.id === activePhase.id),
    0
  );
  const nextPhase = locale.page.phases[activePhaseIndex + 1] || locale.page.phases[activePhaseIndex];
  const phaseCountLabel = interpolateLabel(locale.page.roadmap.phaseCountLabel, {
    count: locale.page.phases.length,
  });
  const phasePositionLabel = interpolateLabel(locale.page.roadmap.phasePositionLabel, {
    current: activePhaseIndex + 1,
    total: locale.page.phases.length,
  });

  useEffect(() => {
    document.title = locale.page.seoTitle;
    window.localStorage.setItem("publicRoadmapLanguage", language);
  }, [language, locale.page.seoTitle]);

  useEffect(() => {
    if (!locale.page.phases.some((phase) => phase.id === activePhaseId)) {
      setActivePhaseId(locale.page.phases[0].id);
    }
  }, [activePhaseId, locale.page.phases]);

  const hero = (
    <>
      <CContainer fluid className="public-core-container public-page-shell public-roadmap-toolbar-shell">
        <div className="public-roadmap-toolbar-slot">
          <div className="public-roadmap-toolbar public-page-callout">
            <div className="public-roadmap-toolbar__actions" role="group" aria-label={locale.page.languageControl?.label}>
              {publicRoadmapContent.languages.map((entry) => (
                <CButton
                  key={entry.code}
                  type="button"
                  className={`public-roadmap-toolbar__language-btn ${
                    entry.code === language ? "is-active" : ""
                  }`.trim()}
                  onClick={() => setLanguage(entry.code)}
                  aria-pressed={entry.code === language}
                >
                  <span>{entry.code.toUpperCase()}</span>
                  <small>{entry.nativeLabel}</small>
                </CButton>
              ))}
            </div>

            <div className="public-roadmap-toolbar__copy">
              <span className="public-roadmap-toolbar__label">
                {locale.page.languageControl?.label || "Page language"}
              </span>
              <p className="public-roadmap-toolbar__helper">
                {locale.page.languageControl?.helper ||
                  "This control changes the full public roadmap surface."}
              </p>
            </div>
          </div>
        </div>
      </CContainer>
      <section className="public-roadmap-hero">
        <CContainer fluid className="public-core-container public-page-shell">
          <div className="public-roadmap-hero__panel public-page-hero-panel">
            <div className="public-roadmap-hero__copy">
              <span className="public-page-overline">{locale.page.hero.eyebrow}</span>
              <h1 className="public-page-title">{locale.page.hero.title}</h1>
              <p className="public-page-body public-roadmap-hero__lead">{locale.page.hero.lead}</p>
            </div>

            <aside className="public-roadmap-hero__meta">
              <article className="public-roadmap-hero__meta-card public-page-callout">
                <strong>{locale.page.hero.missionTitle}</strong>
                <span>{locale.page.hero.missionText}</span>
              </article>
              <article className="public-roadmap-hero__meta-card public-page-callout">
                <strong>{locale.page.hero.stageTitle}</strong>
                <span>{locale.page.hero.stageText}</span>
                <div className="public-roadmap-hero__meta-row">
                  <span className={`public-roadmap-status public-roadmap-status--${activePhase.status}`}>
                    {publicRoadmapContent.statusLabels[activePhase.status][language]}
                  </span>
                  <small>{activePhase.timeframe || activePhase.period}</small>
                </div>
              </article>
            </aside>
          </div>
        </CContainer>
      </section>
    </>
  );

  return (
    <PublicSiteShell
      pageClassName="public-roadmap-page"
      pageProps={{ dir: languageMeta.dir, lang: language }}
      hero={hero}
    >
      <section className="public-core-band public-core-band--light">
        <CContainer fluid className="public-core-container public-page-shell">
          <div className="public-roadmap-stack">
            <div className="public-roadmap-main-grid">
              <article className="public-roadmap-section public-page-card">
                <div className="public-roadmap-section__head">
                  <span className="public-page-overline">{locale.page.roadmap.eyebrow}</span>
                  <h2 className="public-page-section-title">{locale.page.roadmap.title}</h2>
                  <p className="public-page-body">{locale.page.roadmap.intro}</p>
                </div>

                <div className="public-roadmap-summary-bar" role="list">
                  <article className="public-roadmap-summary-bar__card" role="listitem">
                    <span>{locale.page.roadmap.currentLabel || "Current phase"}</span>
                    <strong>{activePhase.title}</strong>
                    <small>{activePhase.timeframe || activePhase.period}</small>
                  </article>
                  <article className="public-roadmap-summary-bar__card" role="listitem">
                    <span>{locale.page.roadmap.nextLabel || "Next phase"}</span>
                    <strong>{nextPhase.title}</strong>
                    <small>{nextPhase.timeframe || nextPhase.period}</small>
                  </article>
                  <article className="public-roadmap-summary-bar__card" role="listitem">
                    <span>{locale.page.roadmap.horizonLabel || "Roadmap horizon"}</span>
                    <strong>
                      {locale.page.phases[0].timeframe || locale.page.phases[0].period} -{" "}
                      {locale.page.phases[locale.page.phases.length - 1].timeframe ||
                        locale.page.phases[locale.page.phases.length - 1].period}
                    </strong>
                    <small>{phaseCountLabel}</small>
                  </article>
                </div>

                <div className="public-roadmap-phase-browser">
                  <nav className="public-roadmap-phase-browser__rail" aria-label={locale.page.roadmap.title}>
                    {locale.page.phases.map((phase, index) => (
                      <button
                        key={phase.id}
                        type="button"
                        className={`public-roadmap-phase-browser__trigger ${
                          phase.id === activePhase.id ? "is-active" : ""
                        }`.trim()}
                        onClick={() => setActivePhaseId(phase.id)}
                        aria-pressed={phase.id === activePhase.id}
                      >
                        <div className="public-roadmap-phase-browser__trigger-top">
                          <span className="public-roadmap-phase-browser__index">
                            {index + 1}
                          </span>
                          <span className="public-roadmap-phase-browser__status">
                            {publicRoadmapContent.statusLabels[phase.status][language]}
                          </span>
                        </div>
                        <strong>{phase.title}</strong>
                        <span className="public-roadmap-phase-browser__time">
                          {phase.timeframe || phase.period}
                        </span>
                        <small>{phase.period}</small>
                      </button>
                    ))}
                  </nav>

                  <div className="public-roadmap-phase-browser__detail public-page-callout">
                    <div className="public-roadmap-phase-browser__detail-head">
                      <div className="public-roadmap-phase-browser__detail-context">
                        <span className="public-roadmap-phase-browser__detail-index">
                          {phasePositionLabel}
                        </span>
                        <h3 className="public-page-subtitle">{activePhase.title}</h3>
                      </div>

                      <div className="public-roadmap-phase-browser__detail-meta">
                        <span className={`public-roadmap-status public-roadmap-status--${activePhase.status}`}>
                          {publicRoadmapContent.statusLabels[activePhase.status][language]}
                        </span>
                        <strong>{activePhase.timeframe || activePhase.period}</strong>
                        <small>{activePhase.period}</small>
                      </div>
                    </div>
                    <p className="public-page-body">{activePhase.summary}</p>

                    <div className="public-roadmap-milestones__head">
                      <span>{locale.page.roadmap.milestonesLabel || "Milestones in this phase"}</span>
                    </div>
                    <div className="public-roadmap-milestones">
                      {activePhase.milestones.map((milestone) => (
                        <article key={milestone.label} className="public-roadmap-milestones__item">
                          <div className="public-roadmap-milestones__time">
                            <strong>{milestone.timeframe || activePhase.timeframe || activePhase.period}</strong>
                          </div>
                          <div className="public-roadmap-milestones__copy">
                            <span className={`public-roadmap-status public-roadmap-status--${milestone.status}`}>
                              {publicRoadmapContent.statusLabels[milestone.status][language]}
                            </span>
                            <p>{milestone.label}</p>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              </article>

              <div className="public-roadmap-guide-slot">
                <PublicRoadmapGuide
                  language={language}
                  locale={locale}
                  languages={publicRoadmapContent.languages}
                />
              </div>

              <article className="public-roadmap-highlight public-roadmap-highlight--pilot public-page-card">
                <span className="public-page-overline">{locale.page.pilot.eyebrow}</span>
                <h2 className="public-page-subtitle">{locale.page.pilot.title}</h2>
                <p className="public-page-body">{locale.page.pilot.body}</p>
                <div className="public-roadmap-highlight__list">
                  {locale.page.pilot.points.map((item) => (
                    <div key={item} className="public-roadmap-highlight__point">
                      <span />
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              </article>
            </div>

            <article className="public-roadmap-outlook public-page-card">
              <div className="public-roadmap-outlook__copy">
                <span className="public-page-overline">{locale.page.outlook.eyebrow}</span>
                <h2 className="public-page-section-title">{locale.page.outlook.title}</h2>
                <p className="public-page-body">{locale.page.outlook.body}</p>
              </div>

              <CButton
                type="button"
                className="public-core-btn public-core-btn--primary public-roadmap-outlook__cta"
                href={locale.page.outlook.ctaHref}
              >
                {locale.page.outlook.ctaLabel}
              </CButton>
            </article>
          </div>
        </CContainer>
      </section>
    </PublicSiteShell>
  );
}
